import { Component } from "vue-property-decorator";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";

import GISagroBase from "./gisagro.base";
import { Agritronics, AgritronicsPhase2, 
         WaterStation, setFloodYears, 
         makeEcoplantLayers, makeExternalWmsWmtsLayers,
         MAP_LAYERS } from "./data/map.layers";
const LAND_SCHEMA = require("../../public/static/js/schema.land4map").default;

import Map from "@/components/map/Map";
import { IMapLayer, IMapViewInfo, IMapAction, 
         IGeoJSON, IFeatureInfo, IInfoSchema, IInfoProperty, IOverlay, 
         Extent, IPopUpInfo, IMapLayerSource
       } from "@/components/map/types";
import { IFileGaf, IUploadFile, ITimeRange, 
         IIconTool, IIconMenu, IReportable } from "@/components/shared/types";
import { User } from "@/components/User";
import { sortObjectArray, createDate, 
         DateOfNDaysBefore, DateOfNMonthsBefore, 
         SameDateOfNMonthsBefore } from "@/common/utils";
import { L,D,DT } from "@/common/filters";

import { MONTHS, LEGEND08, LEGEND24 } from "./data/ecoplant";
import { setPriority } from 'os';

type EEcoPlantType = "cassava" | "maize" | "sugarcane" | "rice";

type EDrawerHelperPage = "" | "help" | "login" | 
  "user_profile" | "user_register" | "user_forgotpassword" | 
  "image_category" | "image_archive" | "image_uploader" | "image_locate" | 
  "station_summary" | "station_charts" | "disease_charts" | 
  "admin_orgs" | "admin_org" | "admin_addorg" | "admin_orglogs" | 
  "admin_users" | "admin_adduser" | "admin_userlogs" | "dashboard";

const MENU_FILE : IIconMenu[] = [{
  name: 'menuFileNew',
  icon: 'insert_drive_file'
  },{
    name: 'menuFileLocal',
    icon: 'insert_drive_file',
    icon2: 'computer',
    class: 'theme--dark'
  },{
    name: 'menuFileServer',
    icon: 'insert_drive_file',
    icon2: 'cloud_queue',
    class: 'theme--dark',
    requireLogin: true
  },{
    divider: true
  },{
    name: 'menuFileSave',
    icon: 'save'
  },{
    name: 'menuFileUpload',
    icon: 'cloud_upload',
    requireLogin: true
  },{
    name: 'menuFileShare',
    icon: 'share',
    requireLogin: true
  },{
    divider: true
  },{
    name: 'menuFileDeleteFromServer',
    icon: 'cloud_queue',
    icon2: 'clear',
    class: 'menufile-clear',
    requireLogin: true
  },{
    divider: true
  },{
    name: 'menuFileProperties',
    icon: 'ballot'
  },{
    divider: true
  },{
    name: 'menuFilePrint',
    icon: 'print'
  },{
    name: 'menuFileSaveAsFile',
    icon: 'save_alt',
    submenus: [{
      name: 'menuFileSaveAsPdf',
      icon: 'picture_as_pdf'
    },{
      name: 'menuFileSaveAsPng',
      icon: 'photo_library'
    }]
  }];
const MENU_DATA : IIconMenu[] = [{
    name: 'menuDataLocal',
    icon: 'folder_open',
    icon2: 'computer'
  },{
    name: 'menuDataServer',
    icon: 'folder_open',
    icon2: 'cloud_queue',
    requireLogin: true
  },{
    divider: true
  },{
    name: 'menuDataFilter',
    icon: 'mdi-table-search' //'mdi-filter-outline'
  },{
    name: 'menuDataManage',
    icon: 'mdi-table-edit' //'mdi-database',
    //icon2: 'mdi-calculator', //'mdi-settings-outline',
    //class: 'menudata-manage'
  },{
    divider: true
  },{
    name: 'menuDataExport',
    icon: 'save_alt',
    submenus: [{
      name: 'menuDataExportZip',
      icon: 'fa-file-zip-o'
      //icon: 'filter_none',
      //icon2: 'blur_on',
      //class: 'menudata-export'
    },{
      name: 'menuDataExportExcel',
      icon: 'fa-file-excel-o'
      //icon: 'filter_none',
      //icon2: 'list',
      //class: 'menudata-export'
    }]
  }];
const MENU_CHART : IIconMenu[] = [{
    name: 'menuChartTable',
    icon: 'fa-table'
  },{
    name: 'menuChartPivot',
    icon: 'fa-indent'
  },{
    name: 'menuChartTableSum',
    icon: 'fa-tasks'
  },{
    name: 'menuChartBars',
    icon: 'fa-bar-chart'
  },{
    name: 'menuChartLines',
    icon: 'fa-line-chart'
  },{
    name: 'menuChartAreas',
    icon: 'fa-area-chart'
  },{
    name: 'menuChartPie',
    icon: 'fa-pie-chart'
  },{
    name: 'menuChartDonut',
    icon: 'data_usage'  // mdi
  },{
    name: 'menuChartGauge',
    icon: 'fa-tachometer'
  },{
    name: 'menuChartComposite',
    icon: 'fa-bar-chart',
    icon2: 'show_chart',
    class: 'menuchart-equals'
  },{
    name: 'menuChartScatter',
    icon: 'scatter_plot'
  },{
    name: 'menuChartBoxPlot',
    icon: 'settings_input_component'
  },{
    name: 'menuChartPallel',
    icon: 'done_all'
  },{
    name: 'menuChartTreemap',
    icon: 'dashboard' // mdi
  }];

const END_USER_MENUS = [{
  name: 'help', 
  icon: 'picture_as_pdf',
  title: 'helpManual'
  }];

const ADMIN_MENUS = [{
  name: 'admin_users',
  icon: 'supervised_user_circle',
  title: 'systemAdmin'
  }];

const LOGIN_MENUS = {
    name: 'login',
    title: 'login',
    icon: 'lock'
  }

const LOGOUT_MENUS = {
    name: 'logout',
    title: 'logout',
    icon: 'exit_to_app'
  }

const TODAY = new Date();
const STATION_CHARTS_DATETIME_MENU:IIconTool[] = [{
    name: 'today',
    subTitle: TODAY,
    subTitleIsDate: true
  },{
    divider: true
  },{
    name: 'yesterday'
  },{
    name: 'lastNDays',
    nameReplacer: [3]
  },{
    name: 'lastNDays',
    nameReplacer: [7]
  },{
    name: 'lastNDays',
    nameReplacer: [30]
  },{
    name: 'lastNMonths',
    nameReplacer: [3]
  },/*{
    name: 'lastNMonths',
    nameReplacer: [12]
  },*/{
    name: 'lastNYears',
    nameReplacer: [1,'']
  }]
/** Component Class */
@Component
export default class GISagroMap extends GISagroBase {
  UserFile: IFileGaf = null; // todo, for those analytics menus
  UserData: {[key:string] : any}[] = null;
  showMenuFile:boolean = false;
  showMenuData:boolean = false;

  mapLayerStore: IMapLayer[] = MAP_LAYERS;
  
  timeNow: string = null;
  clockInterval: NodeJS.Timer = null;
  
  // for left side drawer
  toc: "map_filter" | "search" | "image_archive" | "mapinfo" | "layers" | 
       "dashboard_menu" | "data_filter" | "data_calculator" = null; //'layers';
  showDrawerLeft: boolean = false; //true;
  showDrawerLeftLast: boolean = true;
  showDrawerRight: boolean = false;
  showDrawerRight2: boolean = false;
  DrawerHelperPage: EDrawerHelperPage = ''; // important since we use .math() for v-if
  menuFile: IIconMenu[] = MENU_FILE;
  menuData: IIconMenu[] = MENU_DATA;
  menuChart: IIconMenu[] = MENU_CHART;
  menuLogin: IIconMenu = LOGIN_MENUS;
  menuLogout: IIconMenu = LOGOUT_MENUS;

  async initMapLayer() {
    try {
      let ds = await this.User.requestResource('mis/dataset_rice');
      makeEcoplantLayers(ds);
      //let floodYears = await this.User.requestResource('mis/flood_years');
      //setFloodYears(floodYears);
    } catch (error) {
      this.alertError('Oops! initMapLayer->makeEcoplantLayers() got ' + error.message || 'Error');
    }
    try {
      this.fetchCropSuitability();
    } catch(error) {}
    
    let wmsWmts;
    try {
      wmsWmts = await this.User.requestResource('mis/admin_layers');
    } catch (error) {}
    makeExternalWmsWmtsLayers(wmsWmts);

    //let mapLayerStoreRoot: IMapLayer = {
    //  id: 'root.' + this.auid,
    //  title: 'root',
    //  children: MAP_LAYERS
    //}
    MAP_LAYERS.forEach((layer, index) => {
      //layer.parent = mapLayerStoreRoot;
      layer.parent = null;
      layer.id = index + '';
      this.showMapLayer(layer);
    }, this);
  }
  
  toggleGistdaStationLayer(station:"Agritronics" | "AgritronicsPhase2" | "WaterStation") {
    let ms:IMapLayerSource;
    ms = station=='Agritronics' ? Agritronics :
         station=='AgritronicsPhase2' ? AgritronicsPhase2 : WaterStation;
    if (ms.mapLayers) {
      ms.mapLayers.forEach((ml) => {
        ml.visible = ml.checked = !ml.checked;
      });
      ms.visible = ms.mapLayers[0].visible;
    }
    if (ms.nativeLayer) {
      ms.nativeLayer.setVisible(ms.visible);
    }
  }
 
  async fetchSdsData() {
    try {
      this.sds.fetching = true;
      let ok = await this.sds.login('gisagro40','GISagro4.0');
      if (ok) {
        ok = await this.sds.fetchData(); //fetch Source and Stations
        if (ok) {
          let stations = this.sds.Stations;
          [ Agritronics, AgritronicsPhase2, WaterStation ].forEach((station,index) => {
            let s = stations[index+1];
            if (s) { // there was case that no WaterStation is available
              let features = [];
              for (let i=0; i<s.length; i++) {
                features = features.concat(
                  this.$map.GeoJSONToFeatures(s[i].GeoJSON, {
                    dataProjection: 'EPSG:4326',
                    featureProjection: 'EPSG:3857'
                  })
                );
              }
              let source:VectorSource = <VectorSource>station.nativeLayer.getSource();
              if (source['source']) { // source of Clustering
                source = source['source'];
              }
              source.clear();
              source.addFeatures(features);
            }
          }, this);
        }
      }
      this.sds.fetching = false;
    } catch (error) {
      this.sds.fetching = false;
      this.alertError('Oops! Something wrong from fetch station data');
    }
  }

  get Counter() : string[] {
    return [('0000000' + this.PageView).slice(-7)];
  };

  removeLayerToMap(layerId:string) {
    let layers : IMapLayer[] = [];
    this.mapLayersPoint.forEach((layer) => {
      if (layer.id != layerId) {
        layers.push(layer);
      }
    });
    this.mapLayersPoint = layers;
    layers = [];
    this.mapLayersLine.forEach((layer) => {
      if (layer.id != layerId) {
        layers.push(layer);
      }
    });
    this.mapLayersLine = layers;
    layers = [];
    this.mapLayersPolygon.forEach((layer) => {
      if (layer.id != layerId) {
        layers.push(layer);
      }
    });
    this.mapLayersPolygon = layers;
  };

  get menus() {
    return this.ImAdmin ? END_USER_MENUS.concat(ADMIN_MENUS) : END_USER_MENUS;
  };

  get MapViewPaddingLeft() : number {
    return this.showDrawerLeft ? this.TocWidth : 0;
  };

  get MapViewPaddingTop() : number {
    return 52;
  };

  get MapViewPaddingRight() : number {
    return this.showDrawerHelper ? 760 : 0;
  };

  get MapViewPaddingBottom() : number {
    return 0;
  };

  get MapViewPadding() : Extent {
    return [
      this.MapViewPaddingTop,
      this.MapViewPaddingRight, // + (this.BaseMapListIsOpen ? 170 : 70),
      this.MapViewPaddingBottom,
      this.MapViewPaddingLeft
    ];
  };

  get ViewPadding() : {[key:string] : string} {
    return {
      'padding-left' : this.MapViewPaddingLeft + 'px!important',
      'padding-top' : '52px!important',
      'padding-right' : this.MapViewPaddingRight + 'px!important',
      'padding-bottom' : this.MapViewPaddingBottom + 'px!important'
    }
  }

  get menuDataLast5() : IIconMenu[] {
    let menus: IIconMenu[] = null;
    if (this.UserData && this.UserData.length>0) {
      menus = [];
      let i, n = Math.min(5,this.UserData.length);
      for (i=0; i<n; i++) {
        menus.push({
          name: 'data name',
          icon: '',
          icon2: ''
        });
      }
    }
    return menus;
  }

  get showDrawerHelper() : boolean {
    return this.showDrawerLeft ? false : this.showDrawerRight2; // && !this.showDrawerLeft;
  }
  set showDrawerHelper(value:boolean) {
    // do nothing to avoid xxx has no setter error
  }

  DateFormat() {
    return this.language=='th' ? 'dd-mm-yyyy' : 'yyyy-mm-dd';
  };

  // events
  onResize() {
    this.WindowWidth = window.innerWidth;
    if (this.isSmallOrMobile) {
      this.TocWidth = 576;
    } else {
      this.TocWidth = 330;
    }
  };

  async onMapCreated(map:Map) {
    // map is a full component from olMap, including the native olMap
    // BaseMaps, active action, ... and all methods to call
    this.$map = map; // the same as this.$refs.map
    await this.initMapLayer();
    this.$map.createOverlayLayersAndControls(this.mapLayersReversed);
    this.fetchSdsData();
    this.setVisibleLayers();
  };

  onOpenBasemap(open:boolean) {
    this.BaseMapListIsOpen = open;
  };

  /**
   * viewInfo: IMapViewInfo
   */
  onViewChange(viewInfo: IMapViewInfo) {
  };

  buildMapInfo(data:IGeoJSON, editible?:boolean) : IInfoSchema {
    editible = (editible===true);
    let info:IInfoSchema;
    let title = data.featureClass + '';
    let schema:IInfoSchema; 
    let p;
    schema = title.match(/farmland/i) ? LAND_SCHEMA : data.properties.schema;
    if (schema) {
      let callOnOptionChanges:{[key:string]:any}[] = [];
      let crop = {
        type_fk_g: data.properties.type_fk_g,
        genus_fk_g: data.properties.genus_fk_g,
        crop_fk_g: data.properties.crop_fk_g
      };
      info = {
        title: schema.title || data.featureClass || 'Featureinfo',
        saveOrder: schema.saveOrder,
        items: schema.items.map((item,itemIndex) => {
          return {
            group: item.group,
            properties: item.properties.map((prop,propIndex) => {
              let value:any = '', and;
              if (prop.name=='area_sqm') {
                value = parseFloat('0' + data.properties[prop.name]);
                value = (Math.round(value * 100) / 100).toThousandCommaString();
              /* use area_rai_ngan_sqwah, instead
              } else if (prop.name=='area_rai') {
                let hasRai = data.properties['area_rai'] || data.properties['area_rai']==0;
                let hasNgan = data.properties['area_ngan'] || data.properties['area_ngan']==0
                let hasSqWah = data.properties['area_sqwah'] || data.properties['area_sqwah']==0
                if (hasRai) {
                  value = data.properties['area_rai'] + ' ไร่';
                  and = ' ';
                }
                if (hasRai || hasNgan || hasSqWah) {
                  value += and + data.properties['area_ngan'] + ' งาน';
                  and = ' ';
                }
                if (hasSqWah) {
                  value += and + data.properties['area_sqwah'] + ' ตร.วา';
                  and = ' ';
                }*/
              } else if (prop.name.match(/fssn_fk|farmer_ssn/)) {
                value = prop.name=='fssn_fk' ? 
                        data.properties['fssn_fk'] || data.properties['farmer_ssn'] :
                        data.properties['farmer_ssn'] || data.properties['fssn_fk'];
                if (!!value) {
                  let user = this.User;
                  let orgc = data.properties['org_code'] || data.properties['orgc_fk'];
                  if (!(user.isSuperAdmins || user.isAppSupports || (
                      user.Organization && user.Organization.Code && orgc && 
                      user.Organization.Code.match(new RegExp(orgc,'i')) ||
                      user.NationalId==value))) {
                    value = null;
                  }
                }
              } else if (prop.name.match(/deed_no_g/)) {
                value = value || data.properties['deed_no'];
                if (!!value) {
                  let user = this.User;
                  let orgc = data.properties['org_code'] || data.properties['orgc_fk'];
                  if (!(user.isSuperAdmins || user.isAppSupports || (
                      user.Organization && user.Organization.Code && orgc && 
                      user.Organization.Code.match(new RegExp(orgc,'i')) ||
                      user.NationalId==value))) {
                    value = null;
                  }
                }
              } else if (prop.name=='photo') {
                value = data.properties['farmer_ssn'] || data.properties['fssn_fk'];
              } else if (prop.dataType=='boolean') {
                value = data.properties[prop.name]===true ? 1 : 
                        data.properties[prop.name]===false ? 0 : null;
              } else if (prop.dataType=='date') {
                value = new Date(data.properties[prop.name]);
              } else {
                value = data.properties[prop.name];
              }

              if (prop.ctrlType=='select') {
                if (prop.loadOptions && !(prop.options && prop.options.length>0)) {
                  prop.loadOptions(this.User, value);
                }
                if (value && prop.onOptionChange) {
                  callOnOptionChanges.push(prop);
                }
              } else if (prop.ctrlType=='property') {
                if (prop.loadOptions) {
                  prop.loadOptions(this.User, data.properties['gid'], this.cropSuitability);
                } else if (prop.setProperty) {
                  prop.setProperty(crop, value);
                  value = prop.value;
                }
              } else if (prop.loadOptions) {
                prop.loadOptions(this.User, data.properties['gid']);
              } else if (prop.setProperty) {
                prop.setProperty(crop, value);
              }
              return Object.assign(prop, {
                itemIndex: itemIndex,
                propertyIndex: propIndex,
                user: this.User,
                value: value
              });
            })
          };
        })
      };
      callOnOptionChanges.forEach((prop) => {
        try {
          let properties = info.items[prop.itemIndex].properties;
          prop.onOptionChange(properties);
        } catch (error) {}
      });
    } else {
      let props: IInfoProperty[] = [];
      for (p in data.properties) {
        props.push({
          name: p,
          alias: p.toUpperCase(),
          value: data.properties[p]
        });
      }
      info = {
        title: title.capitalize(),
        items: [{
          properties: props
        }]
      }
    }
    info.canEdit = editible;
    if (this.showMapInfoTab>=info.items.length) {
      this.showMapInfoTab = 0;
    }
    return info;
  }

  getInfoPhotoUrl(prop) : string {
    if (prop.photoUrl) {
      return this.appUrl + prop.photoUrl() + '?APIID=th.co.softoo.rest&width=96&height=96';
    } else {
      return null;
    }
  }

  /**
   * GISTDA stations clustering
   * @param info 
   */
  SelectedGistdaStation: Feature = null;
  showClusterFeatures(info:IFeatureInfo) {
    let loc, extent:Extent;
    if (info.features.length==1) {
      this.SelectedGistdaStation = info.features[0];
      loc = info.features[0].getGeometry()['getCoordinates']();
      let p, props = info.features[0].getProperties();
      let title = props.StationLabel + ' (' + props.SourceName + ')';
      let infoList:IPopUpInfo[] = [];
      for (p in props) {
        if (!p.match(/StationLabel|SourceName|geometry/)) {
          infoList.push({
            infoid: p,
            info: props[p]
          });
        }
      }
      let self = this;
      infoList.push({
        infoid: 'Station Images',
        info: 'loading...'
      });
      this.$map.showInfoPopup(title, infoList, {
        position: loc,
        //style: 'max-width:400px',
        infoType: 'props',
        offset: [0,-13],
        methods: {
          async onMounted() {
            let info = infoList[infoList.length - 1];
            try {
              let images = await self.sds.fetchStationImage(props.SourceID, props.StationID);
              if (!images) {
                info.info = L('noImage');
              } else {
                let popup;
                let backup = {
                  closeMe: null,
                  containerStyle: null
                };
                info.info = L('numberOfImages', [images.length]);
                info.actionIcon = 'photo';
                info.data = images;
                info.action = (event) => {
                  let images = event.data.data;
                  sortObjectArray(images,'ObservedDatetime desc');
                  popup = event.target;
                  popup.containerStyle = 'width:600px';
                  popup.info = images.map((image,index) => {
                    return {
                      infoid: '' + index,
                      info: image.ObservedValue,
                      data: {
                        DateTime: image.ObservedDatetime,
                      }
                    }
                  });
                  popup.infoType = 'photos';
                  backup = {
                    closeMe: popup.closeMe,
                    containerStyle: popup.containerStyle
                  };
                  
                  let sourceId = props.SourceID;
                  let width;
                  if (sourceId==1) {
                    width = 720 / 480 * 400;
                  } else if (sourceId==2) {
                    width = 1920 / 1080 * 400;
                  } else {
                    width= 600; //??
                  }
                  popup.containerStyle = 'width:' + width + 'px';
                  popup.closeMe = () => {
                    popup.info = infoList;
                    popup.infoType = 'props';
                    popup.closeMe = backup.closeMe;
                    popup.containerStyle = backup.containerStyle;
                  }
                  setTimeout(()=>{
                    popup.map.panInfoPopupIntoView();
                  },300);
                }
              }
            } catch(error) {
              if (error.status==404) {
                info.info = 'No Image';
              } else {
                info.info = error.message;
              }
            }
          },
          closeMe() {
            if (self.DrawerHelperPage=='station_charts') {
              self.closeDrawerRight2();
            }
            self.$map.hideInfoPopup();
          }
        },
        zoomTo: false
      });
      this.showStationCharts({
        name: 'today',
        subTitle: new Date(),
        subTitleIsDate: true
      });
    } else {
      extent = [Number.MAX_VALUE,Number.MAX_VALUE,-Number.MAX_VALUE,-Number.MAX_VALUE];
      info.features.forEach((feature) => {
        loc = feature.getGeometry()['getCoordinates']();
        extent[0] = Math.min(extent[0],loc[0]);
        extent[1] = Math.min(extent[1],loc[1]);
        extent[2] = Math.max(extent[2],loc[0]);
        extent[3] = Math.max(extent[3],loc[1]);
      });
      this.$map.changeMapView('zoomToExtent',{extent:extent});
    }
  }

  showStationOverview() {
    this.setMapAction({name:'zoomToExtent'});
    let stationLayers:IMapLayer[];
    for (let i=0; i<this.mapLayersPoint.length && !stationLayers; i++) {
      let ml = this.mapLayersPoint[i];
      if (ml.parent && ml.parent.id=='gistdastations') {
        stationLayers = ml.parent.children;
      }
    }
    setTimeout(() => {
      this.RightTocData = {
        sds: this.sds,
        refresher: (new Date()).getTime(), //force to refresh station_summary page
        mapLayers: {
          Agritronics: stationLayers[0], //Agritronics,
          AgritronicsPhase2: stationLayers[1], //AgritronicsPhase2,
          WaterStation: stationLayers[2] //WaterStation
        }
      };
      this.doMenuAction({
        name: 'station_summary',
        title: 'stationAndData',
        icon: 'multiline_chart'
      });
      this.$map.hideInfoPopup();
    }, 0);
  }

  async showStationDetails(action:string, station:IPopUpInfo) {
    let data = station.data;
    let wait = 0;
    if (data.extent) {
      this.$map.changeMapView('zoomToExtent',{extent:data.extent});
      wait = 400;
    }
    setTimeout(() => {
      let infoList:IPopUpInfo[] = [];
      let p;
      let title = data.details.SourceName;
      for (p in data.details) {
        if (p != 'SourceName') {
          infoList.push({
            infoid: p,
            info: data.details[p]
          });
        }
      }
      infoList.push({
        infoid: 'Source Image',
        info: 'loading...'
      });
      this.$map.showInfoPopup(title, infoList, {
        position: data.location,
        style: 'max-width:400px',
        infoType: 'props',
        offset: [0,-10],
        zoomTo: false
      });
    }, wait);
  }
  
  async showFeatureInfo(info:IFeatureInfo) {
    if (info.isCluster) {
      this.showClusterFeatures(info);
      return;
    }
    this.featureInfo = info;
    try {
      this.featureInfo.fetching = true;
      let params:any={}, url = 'gis/';
      let f = info.feature;
      let data:IGeoJSON;
      if (f && f['NATIVE_SOURCE'].NATIVE_LAYERS[0].SOFTOO_SOURCE) {
        let ft = f['NATIVE_SOURCE'].NATIVE_LAYERS[0].SOFTOO_SOURCE.featureType;
        let gid = f.get('gid');
        if (!gid) {
          gid = (f.getId()+'').split('.')[1];
        }
        //params = { gid: gid };
        switch (ft) {
        case 'gisagro:farmland_edit':
          url += 'farmland';
          break;
        case 'gisagro:weather_3hrs': 
          url += 'weather_3hrs';
          break;
        case 'gisagro:weather_3hrs': 
        url += 'weather_3hrs';
          break;
        }
        url += '/' + gid;
      } else {
        if (f) {
          this.$map.clearLayer('SelectedFeatureLayer');
          f = null;
        }
        let d = this.$map.getMetersPerPixel() * 5;
        url += 'features/' + info.location.wgs84[0] + ',' + info.location.wgs84[1] + ',' + d;
      }
      if (url) {
        params.uid = this.User.HalloId || 0;
        data = <IGeoJSON> await this.User.requestResource(url,'GET', params);
        if (data.features && data.type=='FeatureCollection') {
          data = data.features[0];
        }
        data.featureOnMap = this.$map.GeoJSONToFeatures(data, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        })[0];
        if (data.featureClass=='weather_3hrs') {
          let dt = createDate(data.featureOnMap.get('เวลาตรวจวัด'));
          data.featureClass = 'ข้อมูลอากาศ ณ ' + D(dt,'dd MMM hh:mm') + 'น.';
        }
      } else {
        data = this.$map.FeatureToGeoJSON(f, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
        data.featureOnMap = f;
      }
      this.mapInfo = this.buildMapInfo(data, false);
      this.featureInfo.fetching = false;
      this.toc = 'mapinfo';
      this.showDrawerLeft = true;
      if (!data.featureOnMap['NATIVE_SOURCE']) {
        setTimeout(() => {
          this.$map.addFeaturesToLayer([data.featureOnMap], "SelectedFeatureLayer", {
            zoomTo: false,
            resetMapExtent: false
          });
        },200);
      }
    } catch (error) {
      this.featureInfo.fetching = false;
      if (error.id=='not_found' || error.id=='no_content') {
        this.mapInfo = {
          title: 'noData',
          items: []
        }
      }
    }
  };

  // methods
  setMapAction(action: IMapAction) {
    if (!action || !action.name || action.name==this.activeAction) {
      this.activeAction = null;
      this.showMeasurer = false;
      this.$map.setMapAction(null);
    } else if (action.name=='changeBasemap') {
      this.activeBaseMap = action.params.id;
    } else if (action.name=='printMap') {
      this.showMapForPrint("map");
    } else if (action.name.match(/zoom|pan|zoomToExtent/)) {
      // these actions will not be remained
      /*if (action.params && action.params.info) {
        let info = action.params.info;
        this.$map.addFeaturesToLayer([info.feature], 
          this.srhDataSource.match(/DOL|PLACE/) ? 'SearchFeatureLayer' : 'SelectedFeatureLayer', {
          resetMapExtent: false
        });
        this.$map.showInfoPopup(info.title, info.info, info.popup);
      } else {
        this.$map.changeMapView(action.name, action.params);
      }*/
      this.$map.changeMapView(action.name, action.params);
    } else if (action.name=='showLocator') {
      this.showLocator = true;
      if (!this.showLocatorAt) {
        this.showLocatorAt = 'top-center';
      }
    } else {
      // these should be mappint tools.
      // their actions will remain till it is changed
      if (action.name==this.activeAction) {
        action.name = null; // toggle off
      }
      this.activeAction = action.name;
      if (action.name == 'measure') {
        action.params.target = this.$refs.measureResult;
        this.showMeasurer = !!this.activeAction;
        if (!this.showMeasurerAt) {
          this.showMeasurerAt = 'bottom-left';
        }
        setTimeout(() => {
          this.$map.setMapAction(action.name, action.params || {});
        }, 100); // give at least 50ms for UI to be ready
      } else {
        this.showMeasurer = false;
        this.$map.setMapAction(action.name, action.params || {});
      }
    }
  };

  uploadAvatar() {

  };

  showImageOnMap(image) {
    let title, html, loc:[number,number];
    //title = this.SelectedImageCategory['name_' + this.language];
    title = image.tambon + ' ' + image.amphoe + ' ' + image.province;
    //html = '<img src="' + this.appUrl + '/downloads/' + image.file_name + '" style="width:100%" aspect-ratio="1"></img>';
    html = this.appUrl + '/downloads/' + image.file_name;
    loc = [image.exif_lon, image.exif_lat];
    let popupOptions:IOverlay = {
      style: 'width:400px',
      infoType: 'photo',
      position: this.$map.lonLatToMap(loc),
      offset: [0, -2],
      autoPan: true,
      zoomTo: true,
      bubbling: true
    };
    this.$map.showInfoPopup(title, html, popupOptions);
  }

  async showStationCharts(menu:IIconMenu) {
    if (!this.SelectedGistdaStation) {
      return;
    }
    try {
      let subTitle, replacer;
      let props = this.SelectedGistdaStation.getProperties();
      let nDays, nMonths;
      let today, start, end;
      let dateChunks:Date[][]; // if data range is longer than 30 days, chunk it by 30 days;
      let interval: "Hourly" | "Daily" | "DailyChunks";
      today = new Date();
      if (!this.sds.SelectedStationID) {
        this.sds.setSelectedStation(props.SourceID, props.StationID);
      }
      switch (menu.name) {
      case 'today':
        subTitle = '%s, %s';
        replacer = [today, 'hourly'];
        interval = "Hourly";
        start = new Date(today.getTime());
        start.setHours(0,0,0,0);
        end = new Date(today.getTime()); // IMPORTANT to clone it
        end.setHours(23,59,59,0);
        //await this.sds.fetchHourlyAggregate(props.SourceID, props.StationLabel, start, end);
        break;
      case 'yesterday':
        start = DateOfNDaysBefore(1, today);
        subTitle = '%s, %s';
        replacer = [new Date(start.getTime()), 'hourly'];
        interval = "Hourly";
        end = new Date(start.getTime());
        start.setHours(0,0,0,0);
        end.setHours(23,59,59,0);
        //await this.sds.fetchHourlyAggregate(props.SourceID, props.StationLabel, start, end);
        break;
      case 'lastNDays':
        nDays = menu.nameReplacer[0];
        start = DateOfNDaysBefore(nDays, today);
        end = DateOfNDaysBefore(1, today);
        subTitle = '%s - %s, %s';
        if (nDays<=7) {
          start.setHours(0,0,0,0);
          end.setHours(23,59,59,0);
          replacer = [start, end, 'hourly'];
          interval = "Hourly";
          //await this.sds.fetchHourlyAggregate(props.SourceID, props.StationLabel, start, end);
        } else {
          replacer = [start, end, 'daily'];
          interval = "Daily";
          //await this.sds.fetchAggregateOfLast30Days(props.SourceID);
        }
        break;
      case 'lastNYears':
      case 'lastNMonths': // in case of more than 30 days
        nMonths = menu.nameReplacer[0];
        if (menu.name=='lastNYears') {
          nMonths *= 12;
        }
        dateChunks = [];
        end = DateOfNDaysBefore(1, today);
        end.setHours(23,59,59,0);
        let start0 = DateOfNMonthsBefore(nMonths, end);
        start0.setHours(0,0,0,0);
        start = DateOfNDaysBefore(30, today);
        start.setHours(0,0,0,0);
        let i = 0;
        while (end>start0 && start>=start0) {
          dateChunks[i] = [start,end];
          end = DateOfNDaysBefore(1, start);
          start = DateOfNDaysBefore(30, start);
          i++;
        }
        if (end>start0 && start<start0) {
          start = start0;
          dateChunks[i] = [start, end];
        }
        subTitle = '%s - %s, %s';
        replacer = [start0, dateChunks[0][1], 'daily'];
        interval = "DailyChunks";
        break;
      }
      if (interval=='Hourly') {
        await this.sds.fetchHourlyAggregate(props.SourceID, props.StationLabel, start, end);
      } else if (interval=='Daily' ) {
        await this.sds.fetchDailyAggregate(props.SourceID, props.StationLabel, start, end);
      } else {
        await this.sds.fetchDailyAggregateChunk(props.SourceID, props.StationLabel, dateChunks)
      }
      // open charts on the right hand side
      this.doMenuAction({
        name: 'station_charts',
        title: 'stationAndData',
        icon: 'multiline_chart'
      });
      this.RightTocData = {
        sds: this.sds,
        titleReplacer: ['(' + props.StationLabel + ')'],
        subTitle: subTitle,
        subTitleReplacer: replacer
      };

      /*this.DrawerHelperTitleReplacer = ['(' + props.StationLabel + ')'];
      this.DrawerHelperSubTitle = subTitle;
      this.DrawerHelperSubTitleReplacer = replacer;*/
      setTimeout(()=>{
        this.$map.panInfoPopupIntoView();
      },250);
    } catch (error) {
      this.alertError('Oops! Something happened and stopped fetching data from server');
    }
  }

  showUserProfile() {
    this.doMenuAction({
      name: 'show_profile',
      title: this.User.FullName
    });
    this.RightTocData = this.User;
    //this.DrawerHelperSubTitle = 'profile';
  };

  showOnlineUsers() {
    this.doMenuAction({
      name: 'show_online_users',
      title: this.User.FullName
    });
  };

  LeftTocData:Object = {
    $map: this.$map,
    filter: this.mapFilter,
    mapStore: this.mapLayerStore
  }; 
  
  callFromLeftToc(cmd) {
    if (cmd.name=='toggle_toc') {
      this.toggleToc(cmd.data);
    } else if (cmd.name=='set_map_filter') {
      //app filter to all layers and reload visible layers
      let filter = cmd.data;
    } else if (cmd.name=='show_farmland_layer') {
      //emitted from ./toc/search.base.ts
      let farmland = this.mapLayerStore[0].children[0];
      if (!farmland.visible) {
        farmland.checked = farmland.visible = true;
        farmland.mapSources.forEach((ms) => {
          ms.visible = true;
          if (ms.nativeLayer) {
            ms.nativeLayer.setVisible(ms.visible);
          }
        });
        farmland.legend.forEach((item) => {
          if (item.mapSources && item.type) {
            item.checked = true;
            item.visible = !!item.checked;
          }
        }, this);
        this.doMapLayerAction({
          item: farmland,
          action: {
            action: 'changeChecked'
          }
        });
      }
    } else if (cmd.name=='change_map_filter') {
      for (let f in cmd.data) {
        if (f=='Area1' || f=='Area2') {
          for (let a in cmd.data[f]) {
            this.mapFilter[f][a] = cmd.data[f][a];
          }
        } else {
          this.mapFilter[f] = cmd.data[f];
        }
      }
      if (!!this.RightTocData && !!this.RightTocData.filter) {
        //this.RightTocData = this.RightTocData || {};
        //this.RightTocData.filter = cmd.data;
        this.RightTocData.refresher = (new Date()).getTime();
      }
    } else if (cmd.name=='do_map_layer_action') {
      this.doMapLayerAction(cmd.data);
    }
  }
  
  RightTocData: any = null;
  changeRightToc(data) {
    if (data.menu.name=='after_login') {
      if (this.toc=='image_archive') {
        this.doMenuAction({
          name: 'image_category',
          title: 'imageArchive'
        });
      } else {
        this.showUserProfile();
      }
    } else if (data.menu.name=='image_locate') {
      this.showImageOnMap(data.data);
    } else if (data.menu.name=='toggle_station') {
      this.toggleGistdaStationLayer(data.data);
    } else if (data.menu.name=='station_summary') {
      this.showStationOverview();
    } else if (data.menu.name=='station_charts') {
      this.showStationCharts(data.data);
    } else if (data.menu.name=='update_visible_layers') {
      this.setVisibleLayers();
    } else {
      this.doMenuAction(data.menu);
    }
    this.RightTocData = data.data;
  }

  closeRightToc() {
    this.toc = this.tocLast; // || 'layers';
    this.showDrawerLeft = !!this.toc; //this.showDrawerLeftLast;
    this.showDrawerRight2 = !this.showDrawerRight2;
  }
  
  /*closeRightSideDrawer() {
    this.showDrawerLeftLast = this.showDrawerLeft;
    this.showDrawerLeft = false;
    this.showDrawerRight = false;
  }*/

  doMenuAction(menu) {
    if (this.toc!='image_archive') { //already set in toggleToc
      this.tocLast = this.toc; 
    }
    this.showDrawerLeftLast = this.showDrawerLeft;
    this.showDrawerLeft = false;
    this.showDrawerRight = false;
    setTimeout(() => {
      this.showDrawerRight2 = true;
      this.doHelperTool({
        name: menu.name
      });
    }, 0); // give time to close the right most sidedrawer
  };

  doHelperTool(tool: IIconTool) {
    switch (tool.name) {
    case 'help':
      this.DrawerHelperPage = 'help';
      this.RightTocData = { src: '/static/docs/GISagro_Map_User_Manual.pdf' };
      break;
    case 'login':
      this.DrawerHelperPage = 'login';
      break;
    // for end users
    case 'show_profile':
      this.DrawerHelperPage = 'user_profile';
      break;
    case 'user_register':
      this.DrawerHelperPage = 'user_register';
      break;
    case 'user_forgotpassword':
      this.DrawerHelperPage = 'user_forgotpassword';
      break;
    case 'image_category':
      this.DrawerHelperPage = 'image_category';
      break;
    case 'image_archive':
      this.DrawerHelperPage = 'image_archive';
      break;
    case 'image_uploader':
      this.DrawerHelperPage = 'image_uploader';
      break;
    case 'station_summary':
        this.DrawerHelperPage = 'station_summary';
        break;
    case 'station_charts':
      this.DrawerHelperPage = 'station_charts';
      break;
    case 'disease_charts':
      this.DrawerHelperPage = 'disease_charts';
      break;
    // admin users
    case 'admin_users':
      this.DrawerHelperPage = 'admin_users';
      break;
    case 'admin_adduser':
      this.DrawerHelperPage = 'admin_adduser';
      break;
    case 'show_userlogs':
      this.DrawerHelperPage = 'admin_userlogs';
      break;
    case 'dashboard':
      this.DrawerHelperPage = 'dashboard';
      break;
    }
  };

  doMenuFile(menu:IIconMenu) {
    this.showMenuFile = false;

  };

  doMenuData(menu:IIconMenu) {
    this.showMenuData = false;
    if (menu.name=='menuDataFilter') {
      this.toggleToc('data_filter');
    } else if (menu.name=='menuDataManage') {
      this.toggleToc('data_calculator');
    }
  };

  doMenuChat(menu:IIconMenu) {

  };

  // toc for search or land info
  tocLast = null;
  toggleToc(toc) {
    if (toc=='image_archive') {
      this.tocLast = this.toc;
      this.toc = toc; //IMPORTANT: required in changeRightToc -> 'after_login'
      this.showDrawerLeftLast = this.showDrawerLeft;
      this.showDrawerLeft = false;
      if (!(this.User && this.User.hasLoggedIn)) {
        this.doMenuAction(this.menuLogin);
      } else {
        this.doMenuAction({
          name: 'image_category',
          title: 'imageArchive',
          icon: 'mdi-image-multiple'
        });
      }
    } else {
      if (this.toc==toc) {
        if (this.showDrawerLeft) {
          this.showDrawerLeft = false;
          this.toc = null;
        } else {
          this.showDrawerLeft = true;
        }
      } else {
        this.showDrawerLeft = true;
        this.toc = toc;
        //if (!!this.toc.match(/map_filter|search/)) {
          this.LeftTocData = {
            $map: this.$map,
            filter: this.mapFilter,
            mapStore: this.mapLayerStore
          };
        //}
      }
    }
    if (this.toc!='mapinfo') {
      this.$map.clearLayer('SelectedFeatureLayer');
    }
  };

  closeDrawerRight2() {
    this.showDrawerRight2 = false;
    //if (this.toc=='image_archive') {
    //  this.toc = null;
    //}
    this.showDrawerLeft = !!this.toc;
    this.$map.panInfoPopupIntoView();
  }

  doMapLayerAction(data) {
    // data: {item:IMapLayer,action:{}}
    let item:IMapLayer = data.item;
    let action: any = data.action;
    if (!!action.action.match(/changeChecked|changeVisibility|removeLayer/)) {
      if (item.type=='checkbox') {
        // every node of checkbox should have a native map layer associated
        if (item.mapSources) {
          this.setLayervisibility(item.mapSources, item.visible);
        }
      } else if (item.type=='radio') {
        // all nodes of the same radio group share a common map layer
        let cropLayer;
        if (item.mapSources[0].nativeLayer) {
          cropLayer = item.mapSources[0].nativeLayer;
        }
        let sibling = item.parent && item.parent.children ? item.parent.children : this.mapLayerStore;
        // hide all slibings that belong to the same radioGroup
        sibling.forEach((child) => {
          if (child.type=='radio' && child.radioGroup==item.radioGroup) {
            if (child != item) {
              child.checked = false;
              child.visible = false;
              child.mapSources[0].visible = false;
              if (child.mapSources[0].nativeLayer) {
                child.mapSources[0].nativeLayer.setVisible(false);
              }
            }
          }
          /*20190722: following does not work correctly. use above, instead
          if (child!=item && child.type=='radio' && child.radioGroup==item.radioGroup) {
            child.checked = false;
            child.visible = false;
            if (child.mapSources && child.mapSources.length>0) {
              child.mapSources[0].visible = false;
              if (child.mapSources[0].nativeLayer) {
                cropLayer = child.mapSources[0].nativeLayer;
                cropLayer.setVisible(false);
              }
            }
          }*/
        });
        item.mapSources[0].visible = item.visible;
        if (cropLayer) {
          setTimeout(() => {
            let params = item.mapSources[0].params2 || item.mapSources[0].params;
            cropLayer.getSource().updateParams(params);
            cropLayer.setVisible(item.visible);
          },250);
        }
      }
      this.setVisibleLayers();
      if (item.dashboard) {
        if (this.toc!='image_archive') { //already set in toggleToc
          this.tocLast = this.toc; 
        }
        this.showDrawerRight = false;
        if (item.checked) {
          this.showDrawerLeftLast = this.showDrawerLeft;
          this.showDrawerLeft = false;
          this.RightTocData = {
            $map: this.$map,
            filter: this.mapFilter,
            layer: item,
            refresher: (new Date()).getTime()
          };
          setTimeout(() => {
            this.showDrawerRight2 = !!item.checked;
            this.doHelperTool({
              name: 'dashboard'
            });
          }, 0); // give time to close the right most sidedrawer
        } else {
          this.showDrawerRight2 = false;
        }
      }
    } else if (!!action.action.match(/showGistdaStation/i)) {
      this.showStationOverview();
    } else if (!!action.action.match(/showDiseaseCharts/i)) {
      let layers:IMapLayer[];
      for (let i=0; i<this.mapLayersPolygon.length && !layers; i++) {
        let ml = this.mapLayersPolygon[i];
        if (ml.parent && ml.parent.id=='diseasesandpests') {
          layers = ml.parent.children;
        }
      }
      this.RightTocData = {
        $map: this.$map,
        mapLayers: layers,
        mapFilter: this.mapFilter
      };
      this.doMenuAction({
        name: 'disease_charts'
      });
    }
  }

  async logout() {
    try {
      this.closeRightToc();
      await this.User.logout();
      this.User = new User();
      //this.$store.commit('notifyChange', {
      //  Page: 'home'
      //});
    } catch (error) {

    }
  }
}